import { useMemo } from 'react';

import { getFragmentData, SubMissionActorCaseEnum } from '@e-socna/data-layer';
import type { FragmentType, MissionKindEnum } from '@e-socna/data-layer';

import { FolderBusinessEntity_FolderBusinessEntityConsolidation_Fragment } from '../../../pages/Folder/fragments/folder-business-entity-consolidation.fragment';

import { FormVariant } from '../../enums/variant.enum';
import { FolderBusinessEntityAddressFormStep } from '../../FolderBusinessEntity/steps/Address';
import { FolderBusinessEntityCodingConfigFormStep } from '../../FolderBusinessEntity/steps/CodingConfig';
import { FolderBusinessEntityCodingConfigSchema } from '../../FolderBusinessEntity/steps/CodingConfig/schemas/coding-config.schema';
import { ResolvableMissionTypesSelectionFormStep } from '../../ResolvableMissionTypeUsage/steps/Selection';
import { FolderBusinessEntityType } from '../../FolderBusinessEntity/enums/folder-business-entity-type.enum';
import { hasSubMissions } from '../../../app/utils/mission-kind-profile.util';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { MissionFormTab } from '../enums/tab.enum';
import { MissionActorsFormStep } from '../steps/Actors';
import { MissionCollaborationFormStep } from '../steps/Collaboration';
import { MissionGeneralInfoFormStep } from '../steps/GeneralInfo';
import { MissionSubMissionActorsFormStep } from '../steps/SubMissionActors';
import { buildSubMissionActorsSchema } from '../schemas/sub-mission-actors.schema';
import {
  CollaborationGeneralInfoSchema,
  MissionGeneralInfoSchema,
} from '../schemas/general-info.schema';

import { isMissionFormTabShown } from '../utils/mission-form-tabs.util';
import { pickSteps } from '../utils/pick-steps.util';

import { useCodeSuggestion } from './useCodeSuggestion.hook';

interface Params {
  readonly folderConsolidation:
    | FragmentType<
        typeof FolderBusinessEntity_FolderBusinessEntityConsolidation_Fragment
      >
    | undefined;
  readonly formVariant: FormVariant;
  readonly kind: MissionKindEnum;
}

// The steps a mission form is composed of, picked and ordered by each form.
export const useMissionStepDefinitions = ({
  folderConsolidation: pFolderConsolidation,
  formVariant,
  kind,
}: Readonly<Params>) => {
  const isComposite = hasSubMissions(kind);

  const folderConsolidation = getFragmentData(
    FolderBusinessEntity_FolderBusinessEntityConsolidation_Fragment,
    pFolderConsolidation,
  );

  const { missionWorkflows } = useReferencesContext();
  const internalQuotationWorkflowIds = useMemo(
    () =>
      missionWorkflows
        .filter(
          (workflow) =>
            workflow.settings.subMissionActorCase ===
            SubMissionActorCaseEnum.InternalRecharging,
        )
        .map((workflow) => workflow._id),
    [missionWorkflows],
  );

  return useMemo(() => {
    const missionType = {
      step: {
        id: MissionFormTab.Type,
        title: 'Type de Mission',
        isEnabled: true,
      },
      child: (
        <ResolvableMissionTypesSelectionFormStep
          id={MissionFormTab.Type}
          key={MissionFormTab.Type}
        />
      ),
    };

    const missionGeneralInfo = {
      step: {
        id: MissionFormTab.GeneralInfo,
        title: 'Informations Générales',
        isEnabled: true,
        ValidationSchema: isComposite
          ? CollaborationGeneralInfoSchema
          : MissionGeneralInfoSchema,
      },
      child: (
        <MissionGeneralInfoFormStep
          fallbackDescribable={folderConsolidation}
          formVariant={formVariant}
          id={MissionFormTab.GeneralInfo}
          key={MissionFormTab.GeneralInfo}
          type={
            isComposite
              ? FolderBusinessEntityType.Collaboration
              : FolderBusinessEntityType.Mission
          }
        />
      ),
    };

    const missionCollaboration = {
      step: {
        id: MissionFormTab.Collaboration,
        title: 'Collaboration',
        isEnabled: true,
      },
      child: (
        <MissionCollaborationFormStep
          id={MissionFormTab.Collaboration}
          key={MissionFormTab.Collaboration}
        />
      ),
    };

    const subMissionActors = {
      step: {
        id: MissionFormTab.SubMissionActors,
        title: 'Acteurs',
        isEnabled: true,
        ValidationSchema: buildSubMissionActorsSchema(
          internalQuotationWorkflowIds,
        ),
      },
      child: (
        <MissionSubMissionActorsFormStep
          id={MissionFormTab.SubMissionActors}
          isCreation={formVariant === FormVariant.Creation}
          key={MissionFormTab.SubMissionActors}
        />
      ),
    };

    const missionAddress = {
      step: {
        id: MissionFormTab.Address,
        title: 'Adresse',
        isEnabled: true,
      },
      child: (
        <FolderBusinessEntityAddressFormStep
          fallbackAddress={folderConsolidation?.address}
          id={MissionFormTab.Address}
          key={MissionFormTab.Address}
        />
      ),
    };

    const missionInternalActors = {
      step: {
        id: MissionFormTab.InternalActors,
        title: 'Acteurs SOCNA',
        isEnabled: true,
      },
      child: (
        <MissionActorsFormStep
          fallbackActors={folderConsolidation?.internalActors}
          id={MissionFormTab.InternalActors}
          isInternal
          key={MissionFormTab.InternalActors}
          useDirectMutation={formVariant === FormVariant.Modification}
        />
      ),
    };

    const missionExternalActors = {
      step: {
        id: MissionFormTab.ExternalActors,
        title: 'Acteurs Externes',
        isEnabled: true,
      },
      child: (
        <MissionActorsFormStep
          fallbackActors={folderConsolidation?.externalActors}
          id={MissionFormTab.ExternalActors}
          isInternal={false}
          key={MissionFormTab.ExternalActors}
          useDirectMutation={formVariant === FormVariant.Modification}
        />
      ),
    };

    const missionCodingConfig = {
      step: {
        id: MissionFormTab.CodingConfig,
        title: 'Codification',
        isEnabled: true,
        ValidationSchema: FolderBusinessEntityCodingConfigSchema,
      },
      child: (
        <FolderBusinessEntityCodingConfigFormStep
          id={MissionFormTab.CodingConfig}
          key={MissionFormTab.CodingConfig}
          useCodeSuggestion={useCodeSuggestion}
        />
      ),
    };

    return {
      [MissionFormTab.Type]: missionType,
      [MissionFormTab.GeneralInfo]: missionGeneralInfo,
      [MissionFormTab.Collaboration]: missionCollaboration,
      [MissionFormTab.SubMissionActors]: subMissionActors,
      [MissionFormTab.Address]: missionAddress,
      [MissionFormTab.InternalActors]: missionInternalActors,
      [MissionFormTab.ExternalActors]: missionExternalActors,
      [MissionFormTab.CodingConfig]: missionCodingConfig,
    };
  }, [
    folderConsolidation,
    formVariant,
    internalQuotationWorkflowIds,
    isComposite,
  ]);
};

const MISSION_FORM_TABS: readonly MissionFormTab[] = [
  MissionFormTab.Type,
  MissionFormTab.GeneralInfo,
  MissionFormTab.Collaboration,
  MissionFormTab.SubMissionActors,
  MissionFormTab.Address,
  MissionFormTab.InternalActors,
  MissionFormTab.ExternalActors,
  MissionFormTab.CodingConfig,
];

export const useMissionSteps = ({
  stepIdsSubset,
  ...params
}: Readonly<Params & { stepIdsSubset?: readonly MissionFormTab[] }>) => {
  const definitions = useMissionStepDefinitions(params);
  const { kind, formVariant } = params;

  return useMemo(() => {
    const kindTabs = MISSION_FORM_TABS.filter((tab) =>
      isMissionFormTabShown(tab, { kind, formVariant }),
    );

    return pickSteps(
      definitions,
      stepIdsSubset?.length
        ? kindTabs.filter((tab) => stepIdsSubset.includes(tab))
        : kindTabs,
    );
  }, [definitions, formVariant, kind, stepIdsSubset]);
};
